package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.ContentPaste
import androidx.compose.material.icons.filled.EventBusy
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.People
import androidx.compose.material.icons.filled.Spellcheck
import androidx.compose.material.icons.filled.TableChart
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.rosterimport.CellKey
import com.evoapps.evoshift.domain.rosterimport.CellValue
import com.evoapps.evoshift.domain.rosterimport.ClockRange
import com.evoapps.evoshift.domain.rosterimport.ImportCell
import com.evoapps.evoshift.domain.rosterimport.NameMatch
import com.evoapps.evoshift.domain.rosterimport.PersonChoice
import com.evoapps.evoshift.domain.rosterimport.RosterCodeAction
import com.evoapps.evoshift.domain.rosterimport.ShiftTimeParser
import com.evoapps.evoshift.domain.spreadsheet.XlsxReader
import com.evoapps.evoshift.domain.usecase.roster.CopyOpenReason
import com.evoapps.evoshift.domain.usecase.roster.PlannedDaysMode
import com.evoapps.evoshift.spreadsheet.SpreadsheetDropZone
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.billing.PlanLimitDialog
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.ui.util.dayShort
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.viewmodels.LayoutTarget
import com.evoapps.evoshift.viewmodels.RosterImportCodeRow
import com.evoapps.evoshift.viewmodels.RosterImportPersonRow
import com.evoapps.evoshift.viewmodels.RosterImportStep
import com.evoapps.evoshift.viewmodels.RosterImportUiState
import com.evoapps.evoshift.viewmodels.RosterImportViewModel
import com.evoapps.evoshift.widgets.DataRow
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.helpAction
import evoshift.composeapp.generated.resources.*
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Test tags of the roster import screen. */
object RosterImportTags {
    const val PASTE = "roster_import_paste"
    const val READ_PASTED = "roster_import_read_pasted"
    const val SHEET = "roster_import_sheet"
    const val TARGET_NAMES = "roster_import_target_names"
    const val TARGET_FIRST_DAY = "roster_import_target_first_day"
    const val TARGET_HEADER = "roster_import_target_header"
    const val FIRST_DAY_DATE = "roster_import_first_day_date"
    const val LAYOUT_NEXT = "roster_import_layout_next"
    const val CODES_NEXT = "roster_import_codes_next"
    const val PEOPLE_NEXT = "roster_import_people_next"
    const val ADD_ALL_NEW = "roster_import_add_all_new"
    const val GRID = "roster_import_grid"
    const val SKIP_ALL = "roster_import_skip_all"
    const val APPLY = "roster_import_apply"
    const val EDIT_FIELD = "roster_import_edit_field"
    const val UNDO = "roster_import_undo"
    const val DONE = "roster_import_done"
    fun sheetCell(row: Int, column: Int) = "roster_import_sheet_r${row}_c$column"
    fun code(code: String) = "roster_import_code_$code"
    fun codeStart(code: String) = "roster_import_code_start_$code"
    fun codeEnd(code: String) = "roster_import_code_end_$code"
    fun person(row: Int) = "roster_import_person_$row"
    fun gridCell(key: CellKey) = "roster_import_grid_r${key.row}_c${key.column}"
    fun planned(mode: PlannedDaysMode) = "roster_import_planned_${mode.name.lowercase()}"
}

/** The cells of the sheet shown for pointing at the layout (enough to see where the roster is). */
private const val SHEET_ROWS_SHOWN = 80
private const val SHEET_COLUMNS_SHOWN = 45

/** Problem cells, cells left without their person, skipped cells: light fills, navy text (readable). */
private val ProblemFill = Color(0xFFFFD9D6)
private val ProblemBorder = Color(0xFFB3261E)
private val WithoutPersonFill = Color(0xFFFFE8B8)
private val SkippedFill = Color(0xFFE6E6E6)

/**
 * SH-217: the roster builder's "Import the roster from a spreadsheet" ([RosterImportViewModel]).
 * The source (a dropped or picked file, pasted cells), the layout pointed at on the sheet (the names'
 * column, the first day's cell, optionally the row with day numbers), the codes' meanings, who is
 * who, the month with its problem cells, then "Import N shifts" and the outcome with Undo.
 */
@Composable
fun RosterImportScreen(
    viewModel: RosterImportViewModel,
    onNavigateBack: () -> Unit,
    onSeePlans: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val messageText = state.message?.asString()
    LaunchedEffect(messageText) {
        messageText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    state.planLimit?.let { limit ->
        PlanLimitDialog(
            limit = limit,
            onSeePlans = { viewModel.planLimitShown(); onSeePlans() }.takeIf { state.canChoosePlan },
            onDismiss = viewModel::planLimitShown
        )
    }
    state.editing?.let { cell -> EditCellDialog(state, cell, onSave = viewModel::saveEdit, onSkip = { viewModel.toggleSkip(cell.key) }, onDismiss = viewModel::cancelEdit) }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.roster_import_title),
                onBack = onNavigateBack,
                showWorkplace = true,
                actions = listOf(helpAction(HelpPage.ROSTER_IMPORT))
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            val error = state.loadError
            when {
                state.isLoading -> item("loading") { Centered { CircularProgressIndicator(color = DeepNavy) } }
                error != null -> item("error") { com.evoapps.evoshift.widgets.ErrorState(error, onRetry = viewModel::retry) }
                !state.canImport -> item("read_only") { Text(stringResource(Res.string.roster_import_read_only), color = DeepNavy.copy(alpha = 0.75f)) }
                else -> when (state.step) {
                    RosterImportStep.SOURCE -> sourceStep(state, viewModel)
                    RosterImportStep.LAYOUT -> layoutStep(state, viewModel)
                    RosterImportStep.CODES -> codesStep(state, viewModel)
                    RosterImportStep.PEOPLE -> peopleStep(state, viewModel)
                    RosterImportStep.PREVIEW -> previewStep(state, viewModel)
                    RosterImportStep.DONE -> doneStep(state, viewModel, onNavigateBack)
                }
            }
        }
    }
}

@Composable
private fun Centered(content: @Composable () -> Unit) {
    Row(Modifier.fillMaxWidth().padding(32.dp), horizontalArrangement = Arrangement.Center) { content() }
}

@Composable
private fun SectionTitle(text: String, icon: ImageVector) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(icon, contentDescription = null, tint = DeepNavy, modifier = Modifier.size(22.dp))
        Text(text, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.padding(start = 8.dp))
    }
}

@Composable
private fun Muted(text: String, modifier: Modifier = Modifier) =
    Text(text, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.75f), modifier = modifier)

@Composable
private fun Problem(text: String) = Text(text, style = MaterialTheme.typography.bodyMedium, color = ProblemBorder)

@Composable
private fun BackButton(label: String, onClick: () -> Unit, modifier: Modifier = Modifier) {
    TextButton(onClick = onClick, modifier = modifier) { Text(label, color = DeepNavy) }
}

// --- 1. The source -------------------------------------------------------------------------------

private fun LazyListScope.sourceStep(state: RosterImportUiState, viewModel: RosterImportViewModel) {
    item("intro") { Text(stringResource(Res.string.roster_import_intro), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.85f)) }
    item("drop") {
        SpreadsheetDropZone(
            dropTitle = stringResource(Res.string.staff_import_drop_title),
            chooseTitle = stringResource(Res.string.staff_import_choose_title),
            hint = stringResource(Res.string.staff_import_drop_hint),
            chooseLabel = stringResource(Res.string.staff_import_choose_file),
            onFile = viewModel::onFile,
            isReading = state.isReading,
            readingLabel = stringResource(Res.string.staff_import_reading)
        )
    }
    item("or") { Text(stringResource(Res.string.staff_import_or), color = DeepNavy.copy(alpha = 0.7f), modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center) }
    item("paste") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                SectionTitle(stringResource(Res.string.roster_import_paste_title), Icons.Default.ContentPaste)
                Muted(stringResource(Res.string.roster_import_paste_hint))
                EvoTextField(
                    value = state.pasted,
                    onValueChange = viewModel::onPastedChanged,
                    label = stringResource(Res.string.staff_import_paste_label),
                    singleLine = false,
                    minLines = 4,
                    onLight = true,
                    modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.PASTE)
                )
                OutlinedButton(
                    onClick = viewModel::readPasted,
                    enabled = state.pasted.isNotBlank(),
                    modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.READ_PASTED)
                ) { Text(stringResource(Res.string.staff_import_paste_read), color = DeepNavy) }
            }
        }
    }
    item("unsupported") {
        Row(verticalAlignment = Alignment.Top) {
            Icon(Icons.Default.Info, contentDescription = null, tint = DeepNavy.copy(alpha = 0.7f), modifier = Modifier.size(18.dp))
            Muted(stringResource(Res.string.roster_import_unsupported), Modifier.padding(start = 8.dp))
        }
    }
}

// --- 2. The layout -------------------------------------------------------------------------------

@OptIn(ExperimentalLayoutApi::class)
private fun LazyListScope.layoutStep(state: RosterImportUiState, viewModel: RosterImportViewModel) {
    item("layout_head") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            SectionTitle(stringResource(Res.string.roster_import_layout_title), Icons.Default.TableChart)
            Text(state.fileName ?: stringResource(Res.string.staff_import_paste_label), fontWeight = FontWeight.Bold, color = DeepNavy)
            Muted(stringResource(Res.string.roster_import_layout_hint))
        }
    }
    if (state.sheetNames.size > 1) {
        item("sheets") {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(stringResource(Res.string.staff_import_sheet), style = MaterialTheme.typography.labelLarge, color = DeepNavy)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    state.sheetNames.forEachIndexed { index, name ->
                        FilterChip(
                            selected = index == state.sheetIndex,
                            onClick = { viewModel.selectSheet(index) },
                            label = { Text(name.ifBlank { (index + 1).toString() }) },
                            colors = FilterChipDefaults.filterChipColors(selectedContainerColor = DarkGold.copy(alpha = 0.3f))
                        )
                    }
                }
            }
        }
    }
    item("targets") {
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            TargetChip(
                selected = state.target == LayoutTarget.NAMES,
                title = stringResource(Res.string.roster_import_target_names),
                value = state.nameColumn?.let { stringResource(Res.string.roster_import_names_set, XlsxReader.columnName(it)) }
                    ?: stringResource(Res.string.roster_import_names_unset),
                done = state.nameColumn != null,
                tag = RosterImportTags.TARGET_NAMES,
                onClick = { viewModel.setTarget(LayoutTarget.NAMES) }
            )
            TargetChip(
                selected = state.target == LayoutTarget.FIRST_DAY,
                title = stringResource(Res.string.roster_import_target_first_day),
                value = state.firstCell?.let { stringResource(Res.string.roster_import_first_day_set, cellName(it)) }
                    ?: stringResource(Res.string.roster_import_first_day_unset),
                done = state.firstCell != null,
                tag = RosterImportTags.TARGET_FIRST_DAY,
                onClick = { viewModel.setTarget(LayoutTarget.FIRST_DAY) }
            )
            TargetChip(
                selected = state.target == LayoutTarget.HEADER,
                title = stringResource(Res.string.roster_import_target_header),
                value = state.headerRow?.let { stringResource(Res.string.roster_import_header_set, it + 1) }
                    ?: stringResource(Res.string.roster_import_header_unset),
                done = state.headerRow != null,
                tag = RosterImportTags.TARGET_HEADER,
                onClick = { viewModel.setTarget(LayoutTarget.HEADER) }
            )
            if (state.headerRow != null) {
                TextButton(onClick = viewModel::clearHeaderRow) { Text(stringResource(Res.string.roster_import_header_clear), color = DeepNavy) }
            }
        }
    }
    item("sheet") { SheetGrid(state, onTap = viewModel::onCellTapped) }
    item("first_date") {
        val days = (1..state.monthLength).toList()
        EvoDropdown(
            label = stringResource(Res.string.roster_import_first_day_date),
            options = days,
            selected = state.firstDay,
            onSelected = viewModel::setFirstDay,
            optionLabel = { day -> dayAndMonth(LocalDate(state.year, state.month, day)) },
            supportingText = stringResource(Res.string.roster_import_first_day_date_hint),
            onLight = true,
            modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.FIRST_DAY_DATE)
        )
    }
    item("layout_actions") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            GoldButton(
                text = stringResource(Res.string.roster_import_layout_next),
                onClick = viewModel::confirmLayout,
                enabled = state.layoutComplete,
                modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.LAYOUT_NEXT)
            )
            BackButton(stringResource(Res.string.staff_import_start_over), viewModel::startOver, Modifier.align(Alignment.End))
        }
    }
}

/** "A1" style name of a cell. */
private fun cellName(key: CellKey) = "${XlsxReader.columnName(key.column)}${key.row + 1}"

@Composable
private fun TargetChip(selected: Boolean, title: String, value: String, done: Boolean, tag: String, onClick: () -> Unit) {
    val shape = RoundedCornerShape(12.dp)
    Row(
        Modifier.fillMaxWidth()
            .testTag(tag)
            .border(BorderStroke(if (selected) 2.dp else 1.dp, if (selected) DarkGold else DeepNavy.copy(alpha = 0.25f)), shape)
            .background(if (selected) DarkGold.copy(alpha = 0.12f) else Color.White, shape)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            if (done) Icons.Default.CheckCircle else Icons.Default.TableChart,
            contentDescription = null,
            tint = if (done) DeepNavy else DeepNavy.copy(alpha = 0.5f),
            modifier = Modifier.size(20.dp)
        )
        Column(Modifier.padding(start = 10.dp)) {
            Text(title, fontWeight = FontWeight.Bold, color = DeepNavy, style = MaterialTheme.typography.bodyMedium)
            Text(value, color = DeepNavy.copy(alpha = 0.8f), style = MaterialTheme.typography.bodySmall)
        }
    }
}

/** The sheet as a grid of cells to tap (the first rows and columns), the layout marked on it. */
@Composable
private fun SheetGrid(state: RosterImportUiState, onTap: (Int, Int) -> Unit) {
    val rows = state.rows.take(SHEET_ROWS_SHOWN)
    val width = minOf(rows.maxOfOrNull { it.size } ?: 0, SHEET_COLUMNS_SHOWN)
    val cellWidth = 76.dp
    val first = state.firstCell
    val horizontal = rememberScrollState()
    Box(
        Modifier.fillMaxWidth()
            .heightIn(max = 420.dp)
            .border(1.dp, DeepNavy.copy(alpha = 0.2f), RoundedCornerShape(8.dp))
            .background(Color.White, RoundedCornerShape(8.dp))
            .testTag(RosterImportTags.SHEET)
    ) {
        Column(Modifier.verticalScroll(rememberScrollState()).horizontalScroll(horizontal)) {
            Row {
                GridCell("", 36.dp, header = true)
                (0 until width).forEach { GridCell(XlsxReader.columnName(it), cellWidth, header = true) }
            }
            rows.forEachIndexed { r, row ->
                Row {
                    GridCell("${r + 1}", 36.dp, header = true)
                    (0 until width).forEach { c ->
                        val isName = state.nameColumn == c && (first == null || r >= first.row)
                        val isFirst = first == CellKey(r, c)
                        val isDay = first != null && r >= first.row && c >= first.column
                        val isHeader = state.headerRow == r && (first == null || c >= first.column)
                        val fill = when {
                            isFirst -> DarkGold.copy(alpha = 0.55f)
                            isName -> DarkGold.copy(alpha = 0.25f)
                            isHeader -> DeepNavy.copy(alpha = 0.12f)
                            isDay -> DarkGold.copy(alpha = 0.08f)
                            else -> Color.White
                        }
                        GridCell(
                            text = row.getOrNull(c).orEmpty(),
                            width = cellWidth,
                            fill = fill,
                            bold = isFirst || isHeader,
                            modifier = Modifier.testTag(RosterImportTags.sheetCell(r, c)).clickable { onTap(r, c) }
                        )
                    }
                }
            }
        }
    }
    if (state.rows.size > SHEET_ROWS_SHOWN || (state.rows.maxOfOrNull { it.size } ?: 0) > SHEET_COLUMNS_SHOWN) {
        Muted(stringResource(Res.string.roster_import_sheet_cut, SHEET_ROWS_SHOWN, SHEET_COLUMNS_SHOWN), Modifier.padding(top = 4.dp))
    }
}

@Composable
private fun GridCell(
    text: String,
    width: Dp,
    header: Boolean = false,
    fill: Color = if (header) DeepNavy.copy(alpha = 0.06f) else Color.White,
    bold: Boolean = false,
    textColor: Color = DeepNavy,
    strike: Boolean = false,
    border: Color = DeepNavy.copy(alpha = 0.12f),
    modifier: Modifier = Modifier
) {
    Box(
        modifier
            .width(width)
            .heightIn(min = 34.dp)
            .background(fill)
            .border(0.5.dp, border)
            .padding(horizontal = 4.dp, vertical = 2.dp),
        contentAlignment = Alignment.CenterStart
    ) {
        Text(
            text,
            style = MaterialTheme.typography.bodySmall,
            color = textColor,
            fontWeight = if (bold || header) FontWeight.Bold else FontWeight.Normal,
            textDecoration = if (strike) TextDecoration.LineThrough else null,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}

// --- 3. The codes --------------------------------------------------------------------------------

/** What a code is given, as the menu lists it. */
private enum class CodeKind { NONE, SHIFT, TEMPLATE, DAY_OFF, ABSENCE, IGNORE }

private fun RosterCodeAction?.kind(): CodeKind = when (this) {
    null -> CodeKind.NONE
    is RosterCodeAction.Shift -> CodeKind.SHIFT
    is RosterCodeAction.Template -> CodeKind.TEMPLATE
    RosterCodeAction.DayOff -> CodeKind.DAY_OFF
    is RosterCodeAction.Absence -> CodeKind.ABSENCE
    RosterCodeAction.Ignore -> CodeKind.IGNORE
}

private fun LazyListScope.codesStep(state: RosterImportUiState, viewModel: RosterImportViewModel) {
    item("codes_head") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            SectionTitle(stringResource(Res.string.roster_import_codes_title), Icons.Default.Spellcheck)
            Muted(stringResource(Res.string.roster_import_codes_hint))
        }
    }
    items(state.codes, key = { "code_${it.code}" }) { row -> CodeCard(state, row, onAction = { viewModel.setCodeAction(row.code, it) }) }
    item("codes_actions") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (state.undecidedCodes > 0) Muted(pluralStringResource(Res.plurals.roster_import_codes_undecided, state.undecidedCodes, state.undecidedCodes))
            GoldButton(
                text = stringResource(Res.string.roster_import_codes_next),
                onClick = viewModel::toPeople,
                modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.CODES_NEXT)
            )
            BackButton(stringResource(Res.string.roster_import_back_to_layout), viewModel::backToLayout, Modifier.align(Alignment.End))
        }
    }
}

@Composable
private fun CodeCard(state: RosterImportUiState, row: RosterImportCodeRow, onAction: (RosterCodeAction?) -> Unit) {
    var kind by remember(row.code) { mutableStateOf(row.action.kind()) }
    if (row.action != null && row.action.kind() != kind) kind = row.action.kind()
    RegularCard(Modifier.fillMaxWidth().testTag(RosterImportTags.code(row.code))) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.roster_import_code_quoted, row.code), fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
                Muted(pluralStringResource(Res.plurals.roster_import_cells, row.count, row.count))
            }
            when {
                row.action == null -> Problem(stringResource(Res.string.roster_import_code_undecided))
                row.fromDictionary -> Muted(stringResource(Res.string.roster_import_code_from_dictionary))
                row.suggested -> Muted(stringResource(Res.string.roster_import_code_suggested))
            }
            val kinds = CodeKind.entries.filter { it != CodeKind.TEMPLATE || state.templates.isNotEmpty() }
            EvoDropdown(
                label = stringResource(Res.string.roster_import_code_means),
                options = kinds,
                selected = kind,
                onSelected = { picked ->
                    kind = picked
                    onAction(
                        when (picked) {
                            CodeKind.NONE -> null
                            // The times come from the fields below.
                            CodeKind.SHIFT -> (row.action as? RosterCodeAction.Shift)
                            CodeKind.TEMPLATE -> state.templates.firstOrNull()?.let { RosterCodeAction.Template(it.id) }
                            CodeKind.DAY_OFF -> RosterCodeAction.DayOff
                            CodeKind.ABSENCE -> RosterCodeAction.Absence((row.action as? RosterCodeAction.Absence)?.type ?: AbsenceType.VACATION)
                            CodeKind.IGNORE -> RosterCodeAction.Ignore
                        }
                    )
                },
                optionLabel = { kindLabel(it) },
                onLight = true
            )
            when (kind) {
                CodeKind.SHIFT -> ShiftTimesFields(row, onAction)
                CodeKind.TEMPLATE -> {
                    val selected = (row.action as? RosterCodeAction.Template)?.templateId
                    EvoDropdown(
                        label = stringResource(Res.string.roster_import_template_label),
                        options = state.templates,
                        selected = state.templates.firstOrNull { it.id == selected },
                        onSelected = { onAction(RosterCodeAction.Template(it.id)) },
                        optionLabel = { "${it.name} (${ClockRange(it.startTime, it.endTime).label})" },
                        onLight = true
                    )
                }
                CodeKind.ABSENCE -> {
                    EvoDropdown(
                        label = stringResource(Res.string.roster_import_absence_label),
                        options = AbsenceType.entries,
                        selected = (row.action as? RosterCodeAction.Absence)?.type,
                        onSelected = { onAction(RosterCodeAction.Absence(it)) },
                        optionLabel = { it.label() },
                        onLight = true
                    )
                    Muted(stringResource(Res.string.roster_import_absence_note))
                }
                else -> Unit
            }
        }
    }
}

@Composable
private fun kindLabel(kind: CodeKind): String = stringResource(
    when (kind) {
        CodeKind.NONE -> Res.string.roster_import_action_none
        CodeKind.SHIFT -> Res.string.roster_import_action_shift
        CodeKind.TEMPLATE -> Res.string.roster_import_action_template
        CodeKind.DAY_OFF -> Res.string.roster_import_action_day_off
        CodeKind.ABSENCE -> Res.string.roster_import_action_absence
        CodeKind.IGNORE -> Res.string.roster_import_action_ignore
    }
)

/** The two clock fields of "Shift with times": the code gets them once both read as times. */
@Composable
private fun ShiftTimesFields(row: RosterImportCodeRow, onAction: (RosterCodeAction?) -> Unit) {
    val current = (row.action as? RosterCodeAction.Shift)?.times
    var start by remember(row.code) { mutableStateOf(current?.start.orEmpty()) }
    var end by remember(row.code) { mutableStateOf(current?.end.orEmpty()) }
    fun push() {
        val s = ShiftTimeParser.parseClock(start)
        val e = ShiftTimeParser.parseClock(end, allowMidnightEnd = true)?.let { if (it == "24:00") "00:00" else it }
        onAction(if (s != null && e != null && s != e) RosterCodeAction.Shift(ClockRange(s, e)) else null)
    }
    val invalid = stringResource(Res.string.roster_import_times_invalid)
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        EvoTextField(
            value = start,
            onValueChange = { start = it; push() },
            label = stringResource(Res.string.roster_import_shift_start),
            placeholder = "6:00",
            error = invalid.takeIf { start.isNotBlank() && ShiftTimeParser.parseClock(start) == null },
            onLight = true,
            modifier = Modifier.weight(1f).testTag(RosterImportTags.codeStart(row.code))
        )
        EvoTextField(
            value = end,
            onValueChange = { end = it; push() },
            label = stringResource(Res.string.roster_import_shift_end),
            placeholder = "14:00",
            error = invalid.takeIf { end.isNotBlank() && ShiftTimeParser.parseClock(end, allowMidnightEnd = true) == null },
            onLight = true,
            modifier = Modifier.weight(1f).testTag(RosterImportTags.codeEnd(row.code))
        )
    }
}

// --- 4. The people -------------------------------------------------------------------------------

/** A choice in a person's menu. */
private data class ChoiceOption(val choice: PersonChoice, val name: String? = null)

private fun LazyListScope.peopleStep(state: RosterImportUiState, viewModel: RosterImportViewModel) {
    item("people_head") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            SectionTitle(stringResource(Res.string.roster_import_people_title), Icons.Default.People)
            Muted(stringResource(Res.string.roster_import_people_hint))
            if (state.undecidedPeople > 0) {
                OutlinedButton(onClick = viewModel::addAllUndecidedAsNew, modifier = Modifier.testTag(RosterImportTags.ADD_ALL_NEW)) {
                    Text(stringResource(Res.string.roster_import_add_all_new), color = DeepNavy)
                }
            }
        }
    }
    items(state.people, key = { "person_${it.row}" }) { person -> PersonCard(state, person) { viewModel.setPersonChoice(person.row, it) } }
    item("people_actions") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (state.newPeople > 0) Muted(stringResource(Res.string.roster_import_new_people_hint))
            if (state.undecidedPeople > 0) Problem(stringResource(Res.string.roster_import_people_undecided))
            GoldButton(
                text = stringResource(Res.string.roster_import_people_next),
                onClick = viewModel::toPreview,
                enabled = state.undecidedPeople == 0,
                modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.PEOPLE_NEXT)
            )
            BackButton(
                stringResource(if (state.codes.isEmpty()) Res.string.roster_import_back_to_layout else Res.string.roster_import_back_to_codes),
                if (state.codes.isEmpty()) viewModel::backToLayout else viewModel::backToCodes,
                Modifier.align(Alignment.End)
            )
        }
    }
}

@Composable
private fun PersonCard(state: RosterImportUiState, person: RosterImportPersonRow, onChoice: (PersonChoice) -> Unit) {
    val candidates = (person.match as? NameMatch.Ambiguous)?.uids.orEmpty()
    val team = state.team.sortedWith(compareBy({ it.uid !in candidates }, { it.displayName }))
    val options = listOf(ChoiceOption(PersonChoice.Undecided)) +
        team.map { ChoiceOption(PersonChoice.Member(it.uid), it.displayName) } +
        ChoiceOption(PersonChoice.NewPerson) + ChoiceOption(PersonChoice.Skip)
    RegularCard(Modifier.fillMaxWidth().testTag(RosterImportTags.person(person.row))) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(person.name, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
                Muted(pluralStringResource(Res.plurals.roster_import_cells, person.cells, person.cells))
            }
            val matchText = when (person.match) {
                is NameMatch.Exact -> stringResource(Res.string.roster_import_match_exact)
                is NameMatch.Likely -> stringResource(Res.string.roster_import_match_likely)
                is NameMatch.Ambiguous -> stringResource(Res.string.roster_import_match_ambiguous)
                NameMatch.None -> stringResource(Res.string.roster_import_match_none)
            }
            if (person.choice == PersonChoice.Undecided) Problem(matchText) else Muted(matchText)
            EvoDropdown(
                label = stringResource(Res.string.roster_import_person_is),
                options = options,
                selected = options.firstOrNull { it.choice == person.choice },
                onSelected = { onChoice(it.choice) },
                optionLabel = { option ->
                    when (option.choice) {
                        is PersonChoice.Member -> option.name.orEmpty()
                        PersonChoice.NewPerson -> stringResource(Res.string.roster_import_choice_new)
                        PersonChoice.Skip -> stringResource(Res.string.roster_import_choice_skip)
                        PersonChoice.Undecided -> stringResource(Res.string.roster_import_choice_undecided)
                    }
                },
                onLight = true
            )
        }
    }
}

// --- 5. The month --------------------------------------------------------------------------------

@OptIn(ExperimentalLayoutApi::class)
private fun LazyListScope.previewStep(state: RosterImportUiState, viewModel: RosterImportViewModel) {
    item("preview_head") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SectionTitle(stringResource(Res.string.roster_import_preview_title), Icons.Default.CheckCircle)
                Muted(stringResource(Res.string.roster_import_preview_hint))
                DataRow(stringResource(Res.string.roster_import_count_shifts), state.shiftsToCreate.toString())
                DataRow(stringResource(Res.string.roster_import_count_problems), state.problems.toString())
                if (state.skippedCells > 0) DataRow(stringResource(Res.string.roster_import_count_skipped), state.skippedCells.toString())
                if (state.newPeople > 0) DataRow(stringResource(Res.string.roster_import_count_new_people), state.newPeople.toString())
                val check = state.check
                if (state.isChecking && check == null) {
                    Muted(stringResource(Res.string.roster_import_checking))
                } else if (check != null) {
                    if (check.leftOpenTotal > 0) {
                        DataRow(stringResource(Res.string.roster_import_count_without_person), check.leftOpenTotal.toString())
                        CopyOpenReason.entries.forEach { reason ->
                            val count = check.leftOpen[reason] ?: 0
                            if (count > 0) Muted("• ${stringResource(copyReasonRes(reason), count)}", Modifier.padding(start = 12.dp))
                        }
                    }
                    if (check.skippedClosed > 0) Muted(stringResource(Res.string.roster_import_closed, check.skippedClosed))
                    if (check.replaced.isNotEmpty()) Muted(stringResource(Res.string.roster_import_replaced, check.replaced.size))
                    if (check.labourWarnings > 0) Muted(stringResource(Res.string.copy_from_labour, check.labourWarnings))
                }
            }
        }
    }
    if (state.daysWithShifts.isNotEmpty()) {
        item("planned") {
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(
                    stringResource(Res.string.roster_import_planned_title, state.daysWithShifts.size, state.daysWithShifts.values.sum()),
                    color = DeepNavy, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold
                )
                PlannedDaysMode.entries.forEach { mode ->
                    FilterChip(
                        selected = state.plannedDays == mode,
                        onClick = { viewModel.setPlannedDays(mode) },
                        label = {
                            Text(
                                stringResource(
                                    when (mode) {
                                        PlannedDaysMode.SKIP -> Res.string.roster_import_planned_skip
                                        PlannedDaysMode.ADD -> Res.string.roster_import_planned_add
                                        PlannedDaysMode.REPLACE -> Res.string.roster_import_planned_replace
                                    }
                                ),
                                color = DeepNavy
                            )
                        },
                        colors = FilterChipDefaults.filterChipColors(selectedContainerColor = DarkGold.copy(alpha = 0.3f)),
                        modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.planned(mode))
                    )
                }
            }
        }
    }
    item("legend") {
        FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Legend(ProblemFill, stringResource(Res.string.roster_import_legend_problem))
            Legend(WithoutPersonFill, stringResource(Res.string.roster_import_legend_without_person))
            Legend(SkippedFill, stringResource(Res.string.roster_import_legend_skipped))
        }
    }
    item("grid") { MonthGrid(state, onCell = viewModel::startEdit) }
    val absences = state.absences
    if (absences.isNotEmpty()) {
        item("absences") {
            RegularCard(Modifier.fillMaxWidth()) {
                Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    SectionTitle(stringResource(Res.string.roster_import_absences_title), Icons.Default.EventBusy)
                    Muted(stringResource(Res.string.roster_import_absence_note))
                    absences.forEach { person ->
                        person.days.groupBy({ it.first }, { it.second }).forEach { (type, days) ->
                            Text(
                                stringResource(Res.string.roster_import_absence_line, person.name, type.label(), days.joinToString(", ") { it.day.toString() }),
                                color = DeepNavy, style = MaterialTheme.typography.bodyMedium
                            )
                        }
                    }
                }
            }
        }
    }
    item("preview_actions") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (state.problems > 0) {
                Problem(stringResource(Res.string.roster_import_fix_first))
                OutlinedButton(onClick = viewModel::skipAllProblems, modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.SKIP_ALL)) {
                    Text(stringResource(Res.string.roster_import_skip_all), color = DeepNavy)
                }
            }
            GoldButton(
                text = pluralStringResource(Res.plurals.roster_import_apply, state.shiftsToCreate, state.shiftsToCreate),
                onClick = viewModel::import,
                enabled = state.canConfirm,
                isBusy = state.isImporting,
                modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.APPLY)
            )
            BackButton(stringResource(Res.string.roster_import_back_to_people), viewModel::backToPeople, Modifier.align(Alignment.End))
        }
    }
}

@Composable
private fun Legend(fill: Color, text: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(14.dp).background(fill).border(0.5.dp, DeepNavy.copy(alpha = 0.3f)))
        Text(text, style = MaterialTheme.typography.bodySmall, color = DeepNavy, modifier = Modifier.padding(start = 6.dp))
    }
}

/** What a cell shows in the month: its shifts' times, else its text as on the sheet. */
@Composable
private fun cellText(cell: ImportCell): String = when (val value = cell.value) {
    is CellValue.Shifts -> value.ranges.joinToString("\n") { it.label }
    else -> cell.text
}

/** The month: a row per imported person, a column per day; problem cells highlighted, tap to fix or skip. */
@Composable
private fun MonthGrid(state: RosterImportUiState, onCell: (CellKey) -> Unit) {
    val plan = state.plan ?: return
    val people = state.people.filter { it.row in state.importedRows }
    val dates = plan.days.entries.sortedBy { it.value }
    val withoutPerson = state.withoutPerson
    // "22:00–6:00" fits on one line.
    val cellWidth = 84.dp
    val problemLabel = stringResource(Res.string.roster_import_legend_problem)
    Box(
        Modifier.fillMaxWidth()
            .heightIn(max = 480.dp)
            .border(1.dp, DeepNavy.copy(alpha = 0.2f), RoundedCornerShape(8.dp))
            .background(Color.White, RoundedCornerShape(8.dp))
            .testTag(RosterImportTags.GRID)
    ) {
        Column(Modifier.verticalScroll(rememberScrollState()).horizontalScroll(rememberScrollState())) {
            Row {
                GridCell("", 132.dp, header = true)
                dates.forEach { (_, date) -> GridCell("${date.day} ${dayShort(date.dayOfWeek)}", cellWidth, header = true) }
            }
            people.forEach { person ->
                val cells = plan.cellsOf(person.row).associateBy { it.key.column }
                Row {
                    val displayName = (person.choice as? PersonChoice.Member)?.uid?.let { uid -> state.team.firstOrNull { it.uid == uid }?.displayName } ?: person.name
                    GridCell(displayName, 132.dp, header = true)
                    dates.forEach { (column, _) ->
                        val cell = cells[column]
                        if (cell == null) {
                            GridCell("", cellWidth)
                        } else {
                            val missing = withoutPerson[cell.key]
                            val (fill, border) = when {
                                cell.skipped -> SkippedFill to DeepNavy.copy(alpha = 0.12f)
                                cell.needsFix -> ProblemFill to ProblemBorder
                                missing != null -> WithoutPersonFill to DeepNavy.copy(alpha = 0.12f)
                                else -> Color.White to DeepNavy.copy(alpha = 0.12f)
                            }
                            val text = cellText(cell)
                            GridCell(
                                text = text,
                                width = cellWidth,
                                fill = fill,
                                border = border,
                                strike = cell.skipped,
                                bold = cell.needsFix,
                                textColor = if (cell.value is CellValue.Shifts || cell.needsFix) DeepNavy else DeepNavy.copy(alpha = 0.75f),
                                modifier = Modifier
                                    .testTag(RosterImportTags.gridCell(cell.key))
                                    .semantics { if (cell.needsFix) contentDescription = "$problemLabel: $text" }
                                    .clickable { onCell(cell.key) }
                            )
                        }
                    }
                }
            }
        }
    }
}

/** A cell to fix: its text (read again on save), why it is a problem, Skip or Include again. */
@Composable
private fun EditCellDialog(
    state: RosterImportUiState,
    cell: ImportCell,
    onSave: (String) -> Unit,
    onSkip: () -> Unit,
    onDismiss: () -> Unit
) {
    var text by remember(cell.key) { mutableStateOf(cell.text) }
    val original = state.rows.getOrNull(cell.key.row)?.getOrNull(cell.key.column).orEmpty().trim()
    val name = state.people.firstOrNull { it.row == cell.key.row }?.name.orEmpty()
    EvoDialog(
        title = stringResource(Res.string.roster_import_edit_title, name, dayAndMonth(cell.date)),
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.roster_import_edit_save), { onSave(text) }),
        neutral = DialogAction(stringResource(if (cell.skipped) Res.string.roster_import_include_cell else Res.string.roster_import_skip_cell), onSkip)
    ) {
        Text(stringResource(Res.string.roster_import_edit_original, cellName(cell.key), original), style = MaterialTheme.typography.bodyMedium)
        when (val value = cell.value) {
            is CellValue.UnknownCode -> Text(stringResource(Res.string.roster_import_problem_unknown_code, value.code), color = MaterialTheme.colorScheme.error)
            is CellValue.MissingTemplate -> Text(stringResource(Res.string.roster_import_problem_missing_template, value.code), color = MaterialTheme.colorScheme.error)
            is CellValue.Overlapping -> Text(stringResource(Res.string.roster_import_problem_overlapping), color = MaterialTheme.colorScheme.error)
            else -> Unit
        }
        state.withoutPerson[cell.key]?.let { reason ->
            Text(
                stringResource(
                    when (reason) {
                        CopyOpenReason.INACTIVE -> Res.string.roster_import_without_person_inactive
                        CopyOpenReason.ABSENT -> Res.string.roster_import_without_person_absent
                        CopyOpenReason.UNAVAILABLE -> Res.string.roster_import_without_person_unavailable
                        CopyOpenReason.DOUBLE_BOOKED -> Res.string.roster_import_without_person_double_booked
                    }
                ),
                style = MaterialTheme.typography.bodySmall
            )
        }
        EvoTextField(
            value = text,
            onValueChange = { text = it },
            label = stringResource(Res.string.roster_import_edit_label),
            supportingText = stringResource(Res.string.roster_import_edit_hint),
            modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.EDIT_FIELD)
        )
    }
}

// --- 6. Done -------------------------------------------------------------------------------------

private fun LazyListScope.doneStep(state: RosterImportUiState, viewModel: RosterImportViewModel, onBack: () -> Unit) {
    val summary = state.summary ?: return
    item("done") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SectionTitle(
                    stringResource(if (state.undone) Res.string.roster_import_undone_title else Res.string.roster_import_done_title),
                    Icons.Default.CheckCircle
                )
                DataRow(stringResource(Res.string.roster_import_done_created), summary.created.toString())
                if (summary.newPeople > 0) DataRow(stringResource(Res.string.roster_import_done_new_people), summary.newPeople.toString())
                val withoutPerson = summary.withoutPerson.values.sum()
                if (withoutPerson > 0) {
                    DataRow(stringResource(Res.string.roster_import_count_without_person), withoutPerson.toString())
                    CopyOpenReason.entries.forEach { reason ->
                        val count = summary.withoutPerson[reason] ?: 0
                        if (count > 0) Muted("• ${stringResource(copyReasonRes(reason), count)}", Modifier.padding(start = 12.dp))
                    }
                }
                if (summary.replaced > 0) DataRow(stringResource(Res.string.roster_import_done_replaced), summary.replaced.toString())
                if (summary.skippedPlanned > 0) DataRow(stringResource(Res.string.roster_import_done_skipped_planned), summary.skippedPlanned.toString())
                if (summary.skippedClosed > 0) DataRow(stringResource(Res.string.roster_import_done_skipped_closed), summary.skippedClosed.toString())
                if (summary.absenceDays > 0) DataRow(stringResource(Res.string.roster_import_done_absences), summary.absenceDays.toString())
                HorizontalDivider(color = DeepNavy.copy(alpha = 0.08f))
                Muted(stringResource(if (state.undone) Res.string.roster_import_undone_hint else Res.string.roster_import_done_hint))
                if (summary.newPeople > 0) Muted(stringResource(Res.string.roster_import_people_stay))
                GoldButton(
                    text = stringResource(Res.string.roster_import_done_back),
                    onClick = onBack,
                    modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.DONE)
                )
                if (!state.undone) {
                    OutlinedButton(
                        onClick = viewModel::undo,
                        enabled = !state.isUndoing,
                        modifier = Modifier.fillMaxWidth().testTag(RosterImportTags.UNDO)
                    ) { Text(stringResource(Res.string.roster_import_undo), color = DeepNavy) }
                }
            }
        }
    }
}
